/* eslint-disable no-invalid-this */
// Original repo: https://github.com/localForage/localForage-memoryStorageDriver

import { DriverCallback } from "../cache.model";

let localForageSerializer: Promise<LocalForageSerializer>;

function getSerializerPromise(localForageInstance: LocalForage): Promise<LocalForageSerializer> {
	if (localForageSerializer) {
		return localForageSerializer;
	}
	if (!localForageInstance || typeof localForageInstance.getSerializer !== "function") {
		return Promise.reject(
			new Error(`localforage.getSerializer() was not available! localforage v1.4+ is required!`)
		);
	}
	localForageSerializer = localForageInstance.getSerializer();
	return localForageSerializer;
}

function executeCallback(promise: Promise<any>, callback: DriverCallback | undefined): void {
	if (!callback) {
		return;
	}

	promise.then(
		(result: any) => {
			callback(null, result);
		},
		(error: any) => {
			callback(error);
		}
	);
}

// config the localStorage backend, using options set in the config.
function _initStorage(options: any): any {
	const storageRepository: Record<string, any> = {};
	const dbInfo: Record<string, any> = {};
	if (options) {
		for (const i in options) {
			if (options.hasOwnProperty(i)) {
				dbInfo[i] = options[i];
			}
		}
	}

	const database = (storageRepository[dbInfo.name] = storageRepository[dbInfo.name] || {});
	const table = (database[dbInfo.storeName] = database[dbInfo.storeName] || {});
	dbInfo.db = table;

	this._dbInfo = dbInfo;

	return getSerializerPromise(this).then((serializer) => {
		dbInfo.serializer = serializer;
	});
}

function clear(callback?: DriverCallback): Promise<void> {
	const promise = this.ready().then(() => {
		const db = this._dbInfo.db;

		for (const id in db) {
			if (db.hasOwnProperty(id)) {
				delete db[id];
			}
		}
	});

	executeCallback(promise, callback);
	return promise;
}

function getItem<T>(id: string, callback?: DriverCallback): Promise<T> {
	const promise = this.ready().then(() => {
		const db = this._dbInfo.db;

		// if key is set with undefined as value we should return undefined. If key is unset we should return null
		return db.hasOwnProperty(id) ? db[id] : null;
	});

	executeCallback(promise, callback);
	return promise;
}

function iterate(iterator: (...args: any[]) => void, callback?: DriverCallback): Promise<any> {
	const promise = this.ready().then(() => {
		const db = this._dbInfo.db;

		let iterationNumber = 1;
		for (const id in db) {
			if (!db.hasOwnProperty(id)) {
				continue;
			}

			let value = db[id];

			value = iterator(value, id, iterationNumber++);

			if (value !== undefined) {
				return value;
			}
		}
	});

	executeCallback(promise, callback);
	return promise;
}

function key(n: number, callback?: DriverCallback): Promise<string> {
	const promise = this.ready().then(() => {
		const db = this._dbInfo.db;
		let result: string | null = null;
		let index = 0;

		for (const id in db) {
			if (!db.hasOwnProperty(id)) {
				continue;
			}
			if (n === index) {
				result = id;
				break;
			}
			index++;
		}

		return result;
	});

	executeCallback(promise, callback);
	return promise;
}

function keys(callback?: DriverCallback): Promise<string[]> {
	const promise = this.ready().then(() => {
		const db = this._dbInfo.db;
		const query: string[] = [];

		for (const id in db) {
			if (db.hasOwnProperty(id)) {
				query.push(id);
			}
		}

		return query;
	});

	executeCallback(promise, callback);
	return promise;
}

function length(callback?: DriverCallback): Promise<number> {
	const promise = (this.keys as typeof keys)().then((ids) => ids.length);

	executeCallback(promise, callback);
	return promise;
}

function removeItem(id: string, callback?: DriverCallback): Promise<any> {
	const promise = this.ready().then(() => {
		const db = this._dbInfo.db;
		if (db.hasOwnProperty(id)) {
			delete db[id];
		}
	});

	executeCallback(promise, callback);
	return promise;
}

function setItem<T>(id: string, value: T, callback?: DriverCallback): Promise<T> {
	const promise = this.ready().then(() => {
		// convert undefined values to null.
		// https://github.com/mozilla/localForage/pull/42
		const modifiedValue = value === undefined ? null : value;
		this._dbInfo.db[id] = modifiedValue;
		return Promise.resolve(modifiedValue);
	});

	executeCallback(promise, callback);
	return promise;
}

export const memoryStorageDriver = {
	_driver: "memoryStorageDriver",
	_initStorage,
	iterate,
	getItem,
	setItem,
	removeItem,
	clear,
	length,
	key,
	keys,
};
